import { ChargerEstimatorData } from "..";
import i18n from "locales/i18n";

const te = i18n.getFixedT(null, "estimator");
export interface RecoveryRedirect {
  search: string;
  message: string;
}

const hasPhoto = (file?: File | null, url?: string) => Boolean(file || url);

const hasPhotos = (data: ChargerEstimatorData) =>
  hasPhoto(data.electricalPanelPhoto, data.electricalPanelPhotoURL) &&
  hasPhoto(data.mainBreakerPhoto, data.mainBreakerPhotoURL);

// In flow order; each check mirrors what that step requires before moving on.
const STEPS: {
  page: string;
  hasAnswers: (data: ChargerEstimatorData) => boolean;
  needsPhotos?: boolean;
}[] = [
  {
    page: "addressPage",
    hasAnswers: (data) =>
      Boolean(data.address?.trim()) &&
      data.latitude != null &&
      data.longitude != null,
  },
  {
    page: "chargerLocation",
    hasAnswers: (data) => Boolean(data.installationType),
  },
  { page: "vehicleType", hasAnswers: (data) => Boolean(data.vehicleType) },
  {
    page: "homeSetup",
    hasAnswers: (data) =>
      Boolean(data.houseType && data.chargerLocation && data.chargerSetup),
  },
  {
    page: "powerAccess",
    hasAnswers: (data) =>
      Boolean(
        data.electricalPanelLocation && data.mainServiceSize && data.wireLength,
      ),
    needsPhotos: true,
  },
  {
    page: "preferences",
    hasAnswers: (data) =>
      Boolean(data.purchaseType) &&
      data.elevator !== undefined &&
      data.pool !== undefined &&
      data.hotTub !== undefined,
  },
  {
    page: "contactInformation",
    hasAnswers: (data) =>
      Boolean(
        data.fullName?.trim() && data.email?.trim() && data.phoneNumber?.trim(),
      ),
  },
];

const PAGES_AFTER_STEPS = new Set(["reviewDetails", "selectEstimate"]);

export const getRecoveryRedirect = (
  page: string,
  data: ChargerEstimatorData,
  hasQuoteState: boolean,
  quoteFallbackPage: string,
): RecoveryRedirect | null => {
  if (page === "quote") {
    return hasQuoteState
      ? null
      : {
          search: `?currentPage=${quoteFallbackPage}`,
          message: te("recovery.pick_again"),
        };
  }

  const stepIndex = STEPS.findIndex((step) => step.page === page);
  const earlierSteps = PAGES_AFTER_STEPS.has(page)
    ? STEPS
    : STEPS.slice(0, Math.max(stepIndex, 0));

  const incomplete = earlierSteps.find(
    (step) => !step.hasAnswers(data) || (step.needsPhotos && !hasPhotos(data)),
  );
  if (!incomplete) {
    return null;
  }

  return {
    search: `?currentPage=${incomplete.page}`,
    message: incomplete.hasAnswers(data)
      ? te("recovery.photos_lost")
      : te("recovery.answers_lost"),
  };
};
